import type { AgentEvent } from '@/frontend/features/AgentChats/events/agentEvents'

export interface TextStreamSegment {
  id: string
  text: string
  firstSeq: number | null
  startedAt: string | null
  messagePhase?: 'commentary' | 'final_answer'
  updates: TextStreamUpdate[]
}

interface TextStreamUpdate {
  key: string
  text: string
  replace: boolean
  seq: number | null
  arrivalIndex: number
}

export function upsertTextSegment(
  segments: TextStreamSegment[],
  event: AgentEvent,
): TextStreamSegment[] {
  const id = event.payload.segmentId || `legacy:${event.turn_id}`
  const delta = event.payload.delta || ''
  const index = segments.findIndex((segment) => segment.id === id)
  const update = {
    key: event.id || `${event.seq ?? 'arrival'}:${event.event_type}`,
    text: delta,
    replace: event.payload.replace === true,
    seq: event.seq,
    arrivalIndex: index === -1 ? 0 : segments[index].updates.length,
  }

  if (index === -1) {
    return [
      ...segments,
      {
        id,
        text: delta,
        firstSeq: event.seq,
        startedAt: event.occurred_at || null,
        messagePhase: event.payload.messagePhase,
        updates: [update],
      },
    ]
  }

  return segments.map((segment, position) => position === index
    ? rebuildTextSegment(segment, update, event.payload.messagePhase)
    : segment)
}

// Action Cable may deliver adjacent deltas out of order. Keep the producer
// sequence for each assistant item and rebuild it deterministically instead of
// trusting browser arrival order. Events without a sequence retain their old
// append order, which preserves the OpenClaw fallback contract.
function rebuildTextSegment(
  segment: TextStreamSegment,
  update: TextStreamUpdate,
  messagePhase?: TextStreamSegment['messagePhase'],
): TextStreamSegment {
  const updates = [...segment.updates, update].sort((left, right) => {
    if (left.seq === null && right.seq === null) return left.arrivalIndex - right.arrivalIndex
    if (left.seq === null) return 1
    if (right.seq === null) return -1
    return left.seq - right.seq
  })
  const text = updates.reduce(
    (value, current) => current.replace ? current.text : value + current.text,
    '',
  )
  const sequenced = updates.flatMap((current) => current.seq === null ? [] : [current.seq])

  return {
    ...segment,
    text,
    firstSeq: sequenced.length > 0 ? Math.min(...sequenced) : null,
    messagePhase: messagePhase || segment.messagePhase,
    updates,
  }
}

// The main thread shows final answer items and the legacy unphased OpenClaw
// stream. Codex commentary remains in the activity/progress surface.
export function currentStreamingText(segments: TextStreamSegment[]): string {
  const segment = [...segments].reverse().find((candidate) => candidate.messagePhase !== 'commentary')
  return stripWorkstationFileEnvelope(segment?.text || '')
}

export function currentCommentaryText(segments: TextStreamSegment[]): string {
  return [...segments].reverse().find((candidate) => candidate.messagePhase === 'commentary')?.text || ''
}

// The delivery envelope is private transport metadata appended to the final
// assistant item. Hide it as soon as its opening tag starts streaming; the
// durable message later replaces it with ActiveStorage attachment cards.
export function stripWorkstationFileEnvelope(text: string): string {
  const openingTag = '<workstation_files>'
  const fullTagIndex = text.indexOf(openingTag)
  if (fullTagIndex >= 0) return text.slice(0, fullTagIndex).trimEnd()

  const possiblePrefixLength = Math.min(openingTag.length - 1, text.length)
  for (let length = possiblePrefixLength; length > 0; length -= 1) {
    if (text.endsWith(openingTag.slice(0, length))) {
      return text.slice(0, -length).trimEnd()
    }
  }

  return text
}
